import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useElena } from '../state';
import { listenBest, warmVoice } from '../lib/listen';
import { canListen } from '../lib/speech';
import { Icon } from './Icon';

/**
 * Bumped to start listening; `seed` = words already heard after "Hej Elena", `wake` = they are
 * also in the mic tap's memory (the real recognizer hears the sentence again from its start).
 */
export type MicSignal = { n: number; seed: string; wake?: boolean } | null;

export interface ComposerProps {
  /** Called with the live transcript while listening, and null when listening stops. */
  onListening?: (transcript: string | null) => void;
  autoFocus?: boolean;
  /** Bumped by the parent (e.g. the mobile mic button) to start listening. */
  listenSignal?: MicSignal;
}

export function Composer({ onListening, autoFocus, listenSignal }: ComposerProps) {
  const { send, connection, thinking } = useElena();
  const [text, setText] = useState('');
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const stopRef = useRef<(() => void) | null>(null);
  const voiceAvailable = canListen();

  const startListening = (seed = '', fromWake = false) => {
    if (listening) {
      stopRef.current?.();
      return;
    }
    setVoiceError(null);
    setListening(true);
    onListening?.('');
    stopRef.current = listenBest({
      seed,
      fromWake,
      onInterim: (t) => onListening?.(t),
      onFinal: (t, alternatives) => send(t, true, alternatives),
      onError: (m) => setVoiceError(m),
      onEnd: () => {
        setListening(false);
        onListening?.(null);
        stopRef.current = null;
      },
    });
  };

  useEffect(() => {
    if (listenSignal) startListening(listenSignal.seed, listenSignal.wake === true);
  }, [listenSignal]);

  useEffect(() => () => stopRef.current?.(), []);

  // engine, a spare key, word list and SDK ready before the mic is pressed
  useEffect(() => {
    warmVoice();
    const timer = window.setInterval(warmVoice, 6 * 60_000);
    return () => window.clearInterval(timer);
  }, []);

  // Alt+Space toggles listening on desktop, from anywhere in the app.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || !e.altKey) return;
      e.preventDefault();
      startListening();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!text.trim()) return;
    send(text, false);
    setText('');
  };

  const offline = connection !== 'online';

  return (
    <div className="composer-wrap">
      <form className="composer" onSubmit={submit}>
        <label htmlFor="composer-input" className="sr-only">
          Poruka za Elenu
        </label>
        <input
          id="composer-input"
          className="composer-input"
          type="text"
          autoComplete="off"
          enterKeyHint="send"
          placeholder={offline ? 'Povezujem se sa Elenom…' : 'Reci ili napiši šta ti treba'}
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus={autoFocus}
          maxLength={2000}
        />
        <button
          type="submit"
          className="icon-btn ghost"
          aria-label="Pošalji"
          disabled={!text.trim() || offline}
        >
          <Icon name="send" stroke={2.2} />
        </button>
        <button
          type="button"
          className={`mic-btn${listening ? ' is-live' : ''}`}
          onClick={() => startListening()}
          disabled={!voiceAvailable || offline}
          aria-pressed={listening}
          aria-label={listening ? 'Završi slušanje' : 'Pričaj sa Elenom (Alt+Space)'}
          title={voiceAvailable ? 'Alt+Space' : 'Glas radi u Chrome-u i Edge-u'}
        >
          <Icon name={listening ? 'stop' : 'mic'} size={22} stroke={2} />
        </button>
      </form>
      <p className="composer-hint" aria-live="polite">
        {voiceError ??
          (thinking
            ? 'Elena razmišlja…'
            : voiceAvailable
              ? 'Alt+Space za glas, Ctrl+K za komande'
              : 'Glas radi u Chrome-u i Edge-u')}
      </p>
    </div>
  );
}
